import { ProtectedRouteUser } from '@/app/guards';
import SideBarLayout from '@/app/layouts/SideBarLayout';
import { COLLAPSIBLE_SIDEBAR_GROUPS } from '@/app/layouts/datasetSidebarItems';
import { useDatasetSidebar } from '@/app/layouts/useDatasetSidebar';
import { useTrans } from '@/shared/hooks/useTranslation';
import { ActivityDrawer } from '@/view/features/observability';
import { QuotaBanner, TrialBanner } from '@/view/features/quota';
import { SidebarWorkspaceFooter } from '@/view/features/workspace';
import { BreadcrumbsComponent } from '@/view/shared/Breadcrumbs';
import { useLiveUpdatesViewModel } from '@/viewmodel/features/etlSource';
import { useRunNotificationsViewModel } from '@/viewmodel/features/observability';
import { useDatasetBreadcrumbsViewModel } from '@/viewmodel/features/project';
import { useSidebarWorkspaceViewModel } from '@/viewmodel/features/workspace';
import { type DatasetContentLayout, useDatasetContentViewModel } from '@/viewmodel/shared/sidebar';
import { Outlet } from 'react-router-dom';

const CONTENT_CLASS: Record<DatasetContentLayout, string> = {
  flush: 'p-0 overflow-hidden',
  scroll: 'overflow-y-auto p-0',
  padded: 'overflow-y-auto p-4',
};

export default function DatasetLayout() {
  const { t } = useTrans();
  useRunNotificationsViewModel();
  useLiveUpdatesViewModel();
  const { visibleItems, activeItem, activeLabel, openItem } = useDatasetSidebar();
  const breadcrumbItems = useDatasetBreadcrumbsViewModel(activeLabel);
  const contentLayout = useDatasetContentViewModel();
  const sidebarWorkspace = useSidebarWorkspaceViewModel();

  const sidebarGroupLabels = {
    'data-modeling': t('dataset.sidebar.groups.workspace'),
    operations: t('dataset.sidebar.groups.operations'),
    'admin-config': t('dataset.sidebar.groups.admin'),
    advanced: t('dataset.sidebar.groups.advanced'),
  };

  return (
    <ProtectedRouteUser needToExist={true}>
      <SideBarLayout
        sideBarItems={visibleItems}
        activeItem={activeItem}
        onSideBarItemClick={openItem}
        contentClassName={CONTENT_CLASS[contentLayout]}
        breadcrumbSlot={<BreadcrumbsComponent items={breadcrumbItems} />}
        bannerSlot={
          <>
            <TrialBanner />
            <QuotaBanner />
          </>
        }
        groupLabels={sidebarGroupLabels}
        collapsibleGroups={COLLAPSIBLE_SIDEBAR_GROUPS}
        sidebarFooter={<SidebarWorkspaceFooter workspace={sidebarWorkspace} />}
      >
        <Outlet />
        <ActivityDrawer />
      </SideBarLayout>
    </ProtectedRouteUser>
  );
}
